<template>
  <div class="areablock-container">
    <el-button class="instrument-info"
      :loading="loading"
      @click.stop="toggle()"
    >
      <div>
        <span
          class="dot"
          :style="{
            backgroundColor: getStatusColor(areaBlockStatus),
          }"
        ></span>
        <span style="color: #ffffff;">{{ area.name }} </span> 
      </div>
      <div>
        <span style="color: #ffffff;">({{ lastUpdateTime }})</span> 
        <span style="color: #303133; font-weight: bold; padding-left: 1.2rem;">{{ area.no }}</span>
      </div>
    </el-button>
    <div class="instrument-grid" v-if="isShow">
      <div
        v-for="(instrument, index) in instruments"
        :key="index"
        class="instrument-card"
      >
        <span
          class="dot"
          :style="{
            backgroundColor: getStatusColor(instrument.status),
          }"
        ></span>
        <div class="instrument-data" :title="`${instrument.instrument_data}`">
          <span :style="informInsturmentStyle(instrument.informStatus)"> {{ shortenInsData(instrument.instrument_data) }} </span>
            <router-link :to="{ name: 'chart', query: { instrument: instrument._id, autoRun: true, autoTable: true } }" target="_blank" class="button inform-btn">chart</router-link>
          <el-button class="button inform-btn" :style="informBtnStyle(instrument.informStatus)" v-if="instrument.informStatus" @click="inform(instrument)">
            {{ instrument.informStatus }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="stylus">
.areablock-container{
  padding-left: 0px !important;
  padding-right: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  border: 1px solid #409EFF;
  margin-bottom: 12px;
}
</style>

<style scoped>
.instrument-data {
  width: 100%; /* or whatever width you want */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 93%;
}
.instrument-grid {
  display: flex;
  flex-wrap: wrap;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.instrument-info{
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #409EFF;
  width: -webkit-fill-available;
  text-align: start;
}
.instrument-card {
  display: flex;
  align-items: center;
  margin: 1px;
  padding: 1px;
  padding-right: 0px;
  padding-left: 1px;
  box-sizing: border-box;
  height: auto;
  width: 305pt;
}
.button.chart-btn {
  padding-top: 1px;
  padding-bottom: 1px;
  padding-left: 3px;
  padding-right: 3px;
  font-size: 75%;
  height: auto;
}
.button.inform-btn {
  padding-top: 1px;
  padding-bottom: 1px;
  padding-left: 3px;
  padding-right: 3px;
  font-size: 80%;
  height: auto;
}
</style>

<script>
import moment from 'moment';
import MomentTimeZone from 'moment-timezone';
import { dotList, getStatusColor } from '@/lib/base/systemAlert';
window.moment = moment;
MomentTimeZone();
export default {
  props: {
    alert: {
      require: true,
    },
    close_alert: {
      require: true,
    },
    area: {
      type: Object,
      require: true,
    },
    use_chart_btn: {
      type: Boolean,
      default: true,
    },
    selectedProject: {
      type: Object,
      require: true,
    },
  },
  computed: {
    areaBlockStatus() {
      const priority = {
        GREEN: 0,
        down: 1,
        WL: 2,
        AL: 3,
        WSL: 4,
      };
      let result = 'GREEN';

      if(this.instruments?.length === 0) return 'down';

      for(let ins of this.instruments){
        const status = ins.status || 'GREEN';
        if((priority[status] ?? priority.GREEN) > priority[result]) {
          result = status;
        }
      }

      return result;
    },
  },
  data() {
    return {
      loading: false,
      instruments: [],
      lastUpdateTime: null,
      timeCountdown: 0,
      isShow: false,
    };
  },
  mounted() {
    this.update();
  },
  methods: {
    getStatusColor,
    update() {
      this.loading = true;
      this.getInstrumentsOutput().finally(() => {
        this.loading = false;
      });
    },
    async getInstrumentsOutput() {
      try {
        const res = await this.$axios({
          category: 'occ',
          method: 'get',
          apiName: `${this.area._id}/getAreaInstruments`,
        });

        const newInstruments = [];
        if (res && res.data && res.data.station) {
          for (const station of res.data.station) {
            if (station.instrument) {
              for (const instrument of station.instrument) {
                newInstruments.push(await this.instrumentCompute(instrument));
              }
            }
          }
        }

        this.instruments = newInstruments;
        await this.isAlert();

        // 找出最大時間
        const timestamps = this.instruments
          .map(ins => ins.last_reading?.TIMESTAMP ? moment(ins.last_reading.TIMESTAMP).valueOf() : 0)
          .filter(ts => ts > 0);
        
        const latestTs = timestamps.length > 0 ? Math.max(...timestamps) : 0;

        // 設定 lastUpdateTime
        if (latestTs > 0) {
          this.lastUpdateTime = moment(latestTs).format('YYYY-MM-DD HH:mm');
        } else {
          this.lastUpdateTime = '無資料';
        }
        
        this.emitStatus();
      } catch (error) {
        console.error(`獲取區域 ${this.area.name} 資料失敗:`, error);
        // 失敗時不更新 instruments，保留舊資料
        // 但可以考慮回報 status 為 down 或保持原樣
        this.emitStatus();
      }
    },
    async instrumentCompute(instrument) {
      try {
        let last_reading = null;
        let rate_reading = null;
        
        if (instrument.last_reading) {
          last_reading = instrument.last_reading.reading2;
        }
        
        const last_reading_val = (last_reading !== null && last_reading !== undefined) ? last_reading : 0;
        const last_reading_str = (last_reading !== null && last_reading !== undefined) ? last_reading.toFixed(2) : '--';

        if (instrument.last_reading && instrument.rate1Hr_reading) {
          const rate1Hr = instrument.rate1Hr_reading.reading2;
          if (rate1Hr !== null && rate1Hr !== undefined) {
            rate_reading = (last_reading_val - rate1Hr).toFixed(2);
          }
        }
        
        const rate_reading_str = rate_reading !== null ? rate_reading : '--';
        
        const name = (instrument.data && instrument.data[0]) ? instrument.data[0].name : '未知';
        const unit = (instrument.data && instrument.data[0]) ? instrument.data[0].unit : '';
        
        instrument.instrument_data = `${name} ${last_reading_str}(${unit})/${rate_reading_str}(${unit}/1hr)`;
      } catch (e) {
        console.error('instrumentCompute error:', e, instrument);
        instrument.instrument_data = '資料處理錯誤';
      }
      
      return instrument;
    },
    async isInform(instrument) {
      try {
        let id = instrument._id;
        let result = await this.$axios({
          category: 'occ',
          method: 'get',
          apiName: `inform/${id}`,
        });
        if (result && result.data && result.data.inform != null) return true;
      } catch (error) {
        console.error(`查詢通報狀態失敗 (${instrument._id}):`, error);
      }
      return false;
    },
    informInsturmentStyle(status) {
      if (status === '已通報') return 'background-color: gray; color: white;';
      return '';
    },
    informBtnStyle(status) {
      if (status === '已通報') return 'background-color: gray; color: white;';
      return '';
    },
    shortenInsData(data) {
      if (!data) return '--';
      return data.length > 40 ? `${data.substring(0, 37)}...` : data;
    },
    async isAlert() {
      try {
        const res = await this.$axios({
          category: 'project',
          method: 'get',
          apiName: `${this.selectedProject._id}/area/${this.area._id}/isAlert`,
        });

        if (!res || !res.data) return;

        for (let alert_result of res.data) {
          let instrument = this.instruments.find(instrument => 
            instrument.data && instrument.data[0] && instrument.data[0].name === alert_result.name,
          );
          if (instrument) {
            if (alert_result.alert) {
              console.log(alert_result);
              let isInform = await this.isInform(instrument);
              instrument.informStatus = (isInform) ? '已通報' : '通報';
              if (!isInform) this.alert(instrument.data[0].name, this.area);
              else this.close_alert(instrument.data[0].name);
            } else {
              instrument.informStatus = null;
            }
          } else {
            if (alert_result.name && alert_result.name.includes('TM')) {
              console.error('找不到instrument:', alert_result.name);
            }
          }
        }
      } catch (error) {
        console.error(`獲取告警狀態失敗 (${this.area.name}):`, error);
      }
    },
    //alert(text){
    //  alert(text);
    //},
    alertStatus(status) {
      return status == 'WSL' || status == 'AL' || status == 'WL';
    },
    async inform(instrument) {
      if (instrument.informStatus === '已通報') return;
      
      try {
        this.close_alert(instrument.data[0].name);
        await this.$axios({
          category: 'occ',
          method: 'put',
          apiName: `inform/${instrument._id}`,
        });
        
        const isInform = await this.isInform(instrument);
        console.log('inform', isInform);
        instrument.informStatus = (isInform) ? '已通報' : '未通報';
        this.$forceUpdate();
      } catch (error) {
        console.error(`通報失敗 (${instrument._id}):`, error);
        this.$notifyError && this.$notifyError(error);
      }
    },
    toggle() {
      this.isShow = !this.isShow;
      this.$emit('toggle');    // 父層據此更新 expandedOrder
    },

    emitStatus() {
      // 只在載入完成後回報，避免初始空陣列被當作 down
      if (!this.loading) {
        this.$emit('status-change', {
          id: this.area._id || this.area.name,
          status: this.areaBlockStatus,
        });
      }
    },
  },
};
</script>
